/**
 * Streak Service
 *
 * Manages user learning streaks, achievements, and streak-related features.
 * Tracks daily learning consistency and streak challenges.
 */

import { SERVICE_NAMES } from '../core/constants';
import { InternalSDKOperations } from '../types';
import { BaseService } from './base-service';
import { NetworkDetectionManager } from '../utils/network-detection-manager';

// Keep in sync with STATIC_ACHIEVEMENT_NAMES in the backend DTO.
const VALID_ACHIEVEMENT_NAMES = [
  'BeginnerJourney',
  'Fireball',
  'Firework',
  'Comet',
  'Meteor',
  'Lightning',
  'Aurora',
  'Nova',
  '1YearAnniversary',
  'Overachiever',
  'BananaChamp',
  'Royalty',
] as const;

function isValidAchievementName(name: string): boolean {
  return (
    (VALID_ACHIEVEMENT_NAMES as readonly string[]).includes(name) ||
    /^\d+YearAnniversary$/.test(name) ||
    /^\d+Days$/.test(name)
  );
}

// Keep in sync with MilestoneReward in ProfileInterfaces.ts (M3).
export interface MilestoneReward {
  bananaPoints?: number;
  streakSavers?: number;
  bpBurst?: { multiplier: number; durationMinutes: number };
}

// Keep in sync with MilestoneHitInfo in ProfileInterfaces.ts (M3).
export interface MilestoneHitInfo {
  milestoneDay: number;
  reward: MilestoneReward;
  achievement?: string;
  boosterExpiresAtUtc?: number;
}

export interface RecordStreakActivityResult {
  currentStreak: number;
  earnedStreakSaverBefore: number;
  earnedStreakSaverAfter: number;
  didEarnStreakSaver: boolean;
  milestoneHit: MilestoneHitInfo | null;
}

/**
 * Service class for managing learning streaks and achievements
 */
export class StreakService extends BaseService {
  protected serviceName = SERVICE_NAMES.STREAK;
  /**
   * Creates a new StreakService instance
   * @param internalOps - Internal SDK operations interface
   * @param networkManager - Optional shared network detection manager
   */
  constructor(
    internalOps: InternalSDKOperations,
    networkManager?: NetworkDetectionManager,
  ) {
    super(internalOps, networkManager);
  }

  async updateAchievements(
    achievements: Array<{
      name:
        | 'BeginnerJourney'
        | 'Fireball'
        | 'Firework'
        | 'Comet'
        | 'Overachiever'
        | 'BananaChamp'
        | 'Royalty'
        | 'Meteor'
        | 'Lightning'
        | 'Aurora'
        | 'Nova'
        | '1YearAnniversary'
        | (string & {}); // allows dynamic names like "2YearAnniversary" or "400Days"
      earned: boolean;
    }>,
    version = 'v1',
  ): Promise<void> {
    if (achievements.length === 0) {
      throw new Error('At least one achievement must be provided');
    }

    const allValid = achievements.every((a) => isValidAchievementName(a.name));
    if (!allValid) {
      throw new Error('Invalid achievement name provided');
    }

    const parameters = { achievements };
    const functionName = 'updateAchievements';

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.STREAK,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateUserCurrentStreak(
    currentStreak: number,
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateUserCurrentStreak';
    const parameters = { currentStreak };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.STREAK,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateUserLastDayPlayed(
    dateLastPlayed: number,
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateUserLastDayPlayed';
    const parameters = { dateLastPlayed };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.STREAK,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateUserLongestStreak(
    longestStreak: number,
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateUserLongestStreak';
    const parameters = { longestStreak };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.STREAK,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateUserStreakRecords(
    streakRecords: string[], // Array of dates in YYYY-MM-DD format
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateUserStreakRecords';
    const parameters = { streakRecords };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.STREAK,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateUserEarnedStreakSaver(
    streakSaver: number,
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateUserEarnedStreakSaver';
    const parameters = { streakSaver };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.STREAK,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async grantChecklistStreakSaver(
    dateKey: string,
    timezone?: string,
    version = 'v1',
  ): Promise<void> {
    const functionName = 'grantChecklistStreakSaver';
    const parameters = { dateKey, ...(timezone ? { timezone } : {}) };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.STREAK,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateStreakSaverAppliedDates(
    appliedDates: string[], // Array of dates in YYYY-MM-DD format
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateStreakSaverAppliedDates';
    const parameters = { appliedDates };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.STREAK,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateUserRealConsecutiveDays(
    realConsecutiveDays: number,
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateUserRealConsecutiveDays';
    const parameters = { realConsecutiveDays };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.STREAK,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateUserStreakSaver(
    streakSaver: {
      earnedStreakSaver: number;
      appliedDates: string[];
      earnedDates: string[];
      realConsecutiveDays: number;
    },
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateUserStreakSaver';
    const parameters = { streakSaver };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.STREAK,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateStreakSaverEarnedDates(
    earnedDates: string[], // Array of dates in YYYY-MM-DD format
    version = 'v1',
  ): Promise<void> {
    const functionName = 'updateStreakSaverEarnedDates';
    const parameters = { earnedDates };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.STREAK,
        functionName,
        parameters,
        version,
      ),
    );
  }

  /**
   * Batch update all streak-related data in a single atomic operation
   * This consolidates multiple individual updates into one database write
   */
  async batchUpdateStreakData(
    streakData: {
      currentStreak: number;
      streakRecords: string[];
      dateLastPlayed: number;
      streakSaver: {
        earnedStreakSaver: number;
        appliedDates: string[];
        earnedDates: string[];
        realConsecutiveDays: number;
      };
      longestStreak?: number; // Optional - only update if current streak is longer
    },
    version = 'v1',
  ): Promise<void> {
    const functionName = 'batchUpdateStreakData';
    const parameters = { streakData };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.STREAK,
        functionName,
        parameters,
        version,
      ),
    );
  }

  /**
   * Record a streak-eligible activity for streak tracking
   * @param data - Streak activity data
   * @param version - API version (default: "v1")
   */
  async recordStreakActivity(
    data: {
      finishedAtUtc: number; // ms UTC
      timezone: string; // IANA timezone
      dateKey: string; // "YYYY-MM-DD" local date at completion
      enableMilestones?: boolean; // opt-in gate; backend skips milestone logic when absent/false
      language?: string; // required when enableMilestones is true; needed for banana-point crediting
    },
    version = 'v1',
  ): Promise<RecordStreakActivityResult | null> {
    const functionName = 'recordStreakActivity';
    const parameters = data;

    const response = await this.executeWithOfflineSupport(
      functionName,
      parameters,
      () =>
        this.internalOps.makeRequest<RecordStreakActivityResult>(
          SERVICE_NAMES.STREAK,
          functionName,
          parameters,
          version,
        ),
    );

    return response?.data ?? null;
  }

  /**
   * Check if user needs migration from old streak system
   * @param version - API version (default: "v1")
   * @returns Migration status
   */
  async checkMigrationStatus(version = 'v1'): Promise<{
    migrated: boolean;
    shouldMigrate: boolean;
  }> {
    const functionName = 'checkMigrationStatus';
    const parameters = {};

    const response = await this.internalOps.makeRequest<{
      migrated: boolean;
      shouldMigrate: boolean;
    }>(SERVICE_NAMES.STREAK, functionName, parameters, version);

    return response.data;
  }

  /**
   * Trigger migration from old streak system to new system
   * @param timezone - IANA timezone string (e.g., "America/New_York")
   * @param version - API version (default: "v1")
   * @returns Migration result
   */
  async triggerUserMigration(
    timezone: string,
    version = 'v1',
  ): Promise<{
    success: boolean;
    migratedMonths: number;
    migratedRecords: number;
    reason?: string;
  }> {
    const functionName = 'triggerUserMigration';
    const parameters = { timezone };

    const response = await this.internalOps.makeRequest<{
      success: boolean;
      migratedMonths: number;
      migratedRecords: number;
      reason?: string;
    }>(SERVICE_NAMES.STREAK, functionName, parameters, version);

    return response.data;
  }

  /**
   * Sync/Recompute streak state from backend
   * This is useful to refresh the streak state on app launch or foreground
   * @param timezone - IANA timezone (required to check gap against "today")
   * @param clientNowUtc - Client timestamp in ms UTC
   * @param version - API version (default: "v1")
   */
  async syncStreakState(
    timezone: string,
    clientNowUtc: number,
    version = 'v1',
  ): Promise<void> {
    const functionName = 'syncStreakState';
    const parameters = { timezone, clientNowUtc };

    await this.internalOps.makeRequest<{}>(
      SERVICE_NAMES.STREAK,
      functionName,
      parameters,
      version,
    );
  }
}
